package devxertech.com.seo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray

private const val ORIGIN = "https://devxertech.com"
private const val ORGANIZATION_ID = "$ORIGIN/#organization"
private const val ORGANIZATION_SCRIPT_ID = "devxer-organization-jsonld"
private const val SERVICE_SCRIPT_ID = "devxer-service-jsonld"
private const val ARTICLE_SCRIPT_ID = "devxer-article-jsonld"
private val jsonLdEncoder = Json

private fun absoluteUrl(value: String): String? = when {
    value.startsWith("https://") || value.startsWith("http://") -> value
    value.startsWith("/") -> "$ORIGIN$value"
    else -> null
}

private fun string(value: String): JsonPrimitive = JsonPrimitive(value)

private fun organizationData(): JsonObject = buildJsonObject {
    put("@context", "https://schema.org")
    put("@type", "Organization")
    put("@id", ORGANIZATION_ID)
    put("name", "Devxer Technologies")
    put("url", "$ORIGIN/")
    put("logo", "$ORIGIN/coloredLogo.svg")
    putJsonArray("sameAs") {
        add(string("https://www.linkedin.com/company/devxertech/"))
        add(string("https://www.facebook.com/devxertech"))
        add(string("https://www.instagram.com/devxertech/"))
    }
    put("address", buildJsonObject {
        put("@type", "PostalAddress")
        put("streetAddress", "101-P, Gulberg II")
        put("addressLocality", "Lahore")
        put("addressRegion", "Punjab")
        put("addressCountry", "PK")
    })
    put("telephone", "+92-305-741-8989")
    put("email", "info@devxertech.com")
}

@Composable
fun SiteOrganizationJsonLd() = JsonLd(ORGANIZATION_SCRIPT_ID, organizationData())

fun chatbotServiceData(): JsonObject = buildJsonObject {
    put("@context", "https://schema.org")
    put("@type", "Service")
    put("@id", "$ORIGIN/chatbot-development#service")
    put("url", "$ORIGIN/chatbot-development")
    put("name", "Custom AI Chatbot & Assistant Development")
    put("serviceType", "AI chatbot and assistant development")
    put("description", "Help your customers find answers and your team handle inquiries more effectively. We build custom AI chatbots and assistants around your business information, website and workflows. Whether you need website support or lead qualification, we start with a clear use case and scope the solution around your requirements.")
    put("provider", buildJsonObject {
        put("@id", ORGANIZATION_ID)
    })
}

@Composable
fun ChatbotServiceJsonLd() = JsonLd(SERVICE_SCRIPT_ID, chatbotServiceData())

fun aiServicesData(): JsonObject = buildJsonObject {
    put("@context", "https://schema.org")
    put("@type", "Service")
    put("@id", "$ORIGIN/ai-services#service")
    put("url", "$ORIGIN/ai-services")
    put("name", "AI Assistant & Automation Development")
    put("serviceType", "Internal AI assistants, business knowledge search and workflow automation")
    put("description", "Help your team find information, handle routine tasks and work more efficiently. We build AI assistants and automation around your business documents, tools and workflows, starting with a clearly defined problem and practical project scope.")
    put("provider", buildJsonObject {
        put("@id", ORGANIZATION_ID)
    })
}

@Composable
fun AiServicesJsonLd() = JsonLd(SERVICE_SCRIPT_ID, aiServicesData())

/** Uses the same descriptions as the visible service page metadata. */
@Composable
fun ServiceJsonLd(route: String, seo: PageSeo, serviceName: String) =
    ServiceJsonLd(route, serviceName, seo.description)

@Composable
fun ServiceJsonLd(route: String, serviceName: String, description: String) = JsonLd(SERVICE_SCRIPT_ID, buildJsonObject {
    put("@context", "https://schema.org")
    put("@type", "Service")
    put("@id", "$ORIGIN$route#service")
    put("url", "$ORIGIN$route")
    put("name", serviceName)
    put("serviceType", serviceName)
    put("description", description)
    put("provider", buildJsonObject { put("@id", ORGANIZATION_ID) })
})

fun blogPostingData(
    slug: String,
    title: String,
    description: String,
    authorName: String,
    imageUrls: List<String>,
    publishedAt: String,
    updatedAt: String?,
): JsonObject? {
    if (slug.isBlank() || title.isBlank() || description.isBlank() || !validIsoDate(publishedAt)) return null
    val canonical = "$ORIGIN/blogs/$slug"
    val images = imageUrls.mapNotNull(::absoluteUrl).distinct()
    return buildJsonObject {
        put("@context", "https://schema.org")
        put("@type", "BlogPosting")
        put("@id", "$canonical#blogposting")
        put("url", canonical)
        put("mainEntityOfPage", buildJsonObject {
            put("@type", "WebPage")
            put("@id", canonical)
        })
        put("headline", title)
        put("description", description)
        if (images.isNotEmpty()) put("image", JsonArray(images.map(::string)))
        if (authorName.isNotBlank()) put("author", buildJsonObject {
            put("@type", "Person")
            put("name", authorName)
        })
        put("publisher", buildJsonObject {
            put("@id", ORGANIZATION_ID)
        })
        put("datePublished", publishedAt)
        if (updatedAt != null && validIsoDate(updatedAt)) put("dateModified", updatedAt)
    }
}

private fun validIsoDate(value: String): Boolean =
    Regex("^\\d{4}-\\d{2}-\\d{2}(?:T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?(?:Z|[+-]\\d{2}:\\d{2}))?$").matches(value)

@Composable
fun JsonLd(scriptId: String = ARTICLE_SCRIPT_ID, data: JsonObject?) {
    val encoded = data?.let { jsonLdEncoder.encodeToString(it) }
        // A script element is raw text in HTML; escaping '<' prevents a value from ending the script.
        ?.replace("<", "\\u003c")
    DisposableEffect(scriptId, encoded) {
        val head = document.head
        val existing = head?.querySelector("script#$scriptId")
        if (encoded == null) {
            existing?.parentNode?.removeChild(existing)
        } else {
            val script = (existing ?: document.createElement("script").also {
                it.setAttribute("id", scriptId)
                it.setAttribute("type", "application/ld+json")
                head?.appendChild(it)
            })
            script.textContent = encoded
        }
        onDispose {
            head?.querySelector("script#$scriptId")?.let { it.parentNode?.removeChild(it) }
        }
    }
}
